import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Funnels() {
    const { hasPermission } = useAuth();
    const canManage = hasPermission("funnels_manage");
    const [funnels, setFunnels] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingFunnel, setEditingFunnel] = useState(null);
    const [name, setName] = useState("");
    const [stages, setStages] = useState([]);

    const load = async () => { const { data } = await api.get("/funnels"); setFunnels(data); };
    useEffect(() => { load(); }, []);

    const openEdit = (f) => {
        if (!canManage) return;
        setEditingFunnel(f);
        setName(f.name);
        setStages(
            f.stages
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((s, i) => ({ ...s, _uid: s.stage_id || `tmp_${i}_${Date.now()}` }))
        );
        setOpen(true);
    };
    const openNew = () => {
        if (!canManage) return;
        setEditingFunnel(null);
        setName("");
        setStages([{ name: "Prospecto", order: 0, color: "#1D4ED8", _uid: `tmp_0_${Date.now()}` }]);
        setOpen(true);
    };

    const addStage = () =>
        setStages([
            ...stages,
            {
                name: "Nueva etapa",
                order: stages.length,
                color: "#1D4ED8",
                _uid: `tmp_${stages.length}_${Date.now()}`,
            },
        ]);
    const removeStage = (i) =>
        setStages(stages.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx })));
    const updateStage = (i, patch) =>
        setStages(stages.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

    const onStageDragEnd = (result) => {
        if (!canManage) return;
        if (!result.destination) return;
        const from = result.source.index;
        const to = result.destination.index;
        if (from === to) return;
        const next = stages.slice();
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setStages(next.map((s, i) => ({ ...s, order: i })));
    };

    const save = async () => {
        if (!canManage) return;
        try {
            // Strip the client-only `_uid` field used by react-dnd and persist order by current index.
            const payload = {
                name,
                stages: stages.map((s, i) => {
                    // eslint-disable-next-line no-unused-vars
                    const { _uid, ...rest } = s;
                    return { ...rest, order: i };
                }),
            };
            if (editingFunnel) await api.patch(`/funnels/${editingFunnel.funnel_id}`, payload);
            else await api.post("/funnels", payload);
            toast.success("Guardado"); setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const remove = async (id) => {
        if (!canManage) return;
        if (!window.confirm("¿Eliminar embudo?")) return;
        try { await api.delete(`/funnels/${id}`); toast.success("Eliminado"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <Layout
            title="Embudos de venta"
            subtitle="Configura las etapas de cada pipeline"
            actions={canManage && <Button onClick={openNew} data-testid="funnel-new-button"><Plus className="w-4 h-4 mr-2"/>Nuevo</Button>}
        >
            <div className="grid md:grid-cols-2 gap-4">
                {funnels.map((f) => (
                    <div key={f.funnel_id} className="bg-white border border-slate-200 rounded-lg p-5">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="font-display font-semibold text-slate-900">{f.name}</h3>
                                {f.description && <p className="text-xs text-slate-500 mt-1">{f.description}</p>}
                            </div>
                            {canManage && (
                                <div className="flex gap-1">
                                    <Button variant="outline" size="sm" onClick={()=>openEdit(f)} data-testid={`funnel-edit-${f.funnel_id}`}>Editar</Button>
                                    <Button variant="ghost" size="icon" onClick={()=>remove(f.funnel_id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {f.stages.slice().sort((a,b)=>a.order-b.order).map((s) => (
                                <span key={s.stage_id} className="text-xs px-2.5 py-1 rounded-full text-white" style={{ background: s.color }}>{s.name}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editingFunnel ? "Editar" : "Nuevo"} embudo</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Nombre</Label><Input value={name} onChange={(e)=>setName(e.target.value)} data-testid="funnel-form-name"/></div>
                        <div>
                            <Label>Etapas</Label>
                            <p className="text-xs text-slate-500 mb-2">Arrastra para reordenar las etapas</p>
                            <DragDropContext onDragEnd={onStageDragEnd}>
                                <Droppable droppableId="funnel-stages">
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className="space-y-2 mt-2"
                                            data-testid="funnel-stages-list"
                                        >
                                            {stages.map((s, i) => (
                                                <Draggable draggableId={s._uid} index={i} key={s._uid}>
                                                    {(prov, snap) => (
                                                        <div
                                                            ref={prov.innerRef}
                                                            {...prov.draggableProps}
                                                            className={`flex items-center gap-2 bg-white border rounded-md px-2 py-1.5 ${snap.isDragging ? "border-orange-400 shadow-md" : "border-slate-200"}`}
                                                            data-testid={`funnel-stage-row-${i}`}
                                                        >
                                                            <button
                                                                type="button"
                                                                {...prov.dragHandleProps}
                                                                className="p-1 text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing touch-none"
                                                                aria-label="Reordenar"
                                                                data-testid={`funnel-stage-handle-${i}`}
                                                            >
                                                                <GripVertical className="w-4 h-4" />
                                                            </button>
                                                            <Input
                                                                value={s.name}
                                                                onChange={(e) => updateStage(i, { name: e.target.value })}
                                                                className="flex-1"
                                                            />
                                                            <input
                                                                type="color"
                                                                value={s.color || "#1D4ED8"}
                                                                onChange={(e) => updateStage(i, { color: e.target.value })}
                                                                className="w-10 h-9 rounded border border-slate-200 cursor-pointer"
                                                            />
                                                            <Button variant="ghost" size="icon" onClick={() => removeStage(i)}>
                                                                <Trash2 className="w-4 h-4 text-red-500" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            <Button variant="outline" size="sm" onClick={addStage} data-testid="funnel-add-stage">
                                                <Plus className="w-4 h-4 mr-1" />Agregar etapa
                                            </Button>
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={()=>setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="funnel-form-save">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
